// Lead detail — full prospect record + conversion links + recent activity.
import type { ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Trash2, ArrowRightCircle, PhoneCall } from "lucide-react";
import { TagsPanel } from "@/components/tags-panel";

const fmtDate = (d?: string | null) => (d ? new Date(d).toLocaleDateString() : null);
const money = (v: any) => (v == null ? null : `$${Number(v).toLocaleString()}`);

function KV({ k, v }: { k: string; v: ReactNode }) {
  if (v === null || v === undefined || v === "" || v === false) return null;
  return (
    <div className="flex justify-between gap-4 border-b py-1.5 last:border-0">
      <span className="text-xs text-muted-foreground">{k}</span>
      <span className="text-right text-sm">{v}</span>
    </div>
  );
}

function Prose({ title, body }: { title: string; body?: string | null }) {
  if (!body) return null;
  return (
    <Card>
      <CardHeader><CardTitle className="text-base">{title}</CardTitle></CardHeader>
      <CardContent><p className="whitespace-pre-wrap text-sm">{body}</p></CardContent>
    </Card>
  );
}

export default function LeadDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const queryKey = ["/leads", id];
  const { data, isLoading } = useQuery({ queryKey, queryFn: async () => (await api.get(`/leads/${id}`)).data, enabled: !!id });
  const users = useQuery({ queryKey: ["/users", "picker"], queryFn: async () => (await api.get("/users")).data.data as { id: string; name: string }[] });
  const ownerName = (uid?: string) => users.data?.find((u) => u.id === uid)?.name;

  async function convert() {
    if (!confirm("Convert this lead into an Account + Contact + Deal?")) return;
    await api.post(`/leads/${id}/convert`);
    qc.invalidateQueries({ queryKey });
    qc.invalidateQueries({ queryKey: ["/leads"] });
  }
  async function del() {
    if (!data || !confirm(`Delete lead "${data.name}"?`)) return;
    await api.delete(`/leads/${id}`);
    qc.invalidateQueries({ queryKey: ["/leads"] });
    navigate("/leads");
  }
  async function addToQueue() {
    if (!confirm("Add this lead to your call queue? You'll become the owner.")) return;
    await api.post("/call-queue/add", { kind: "lead", ids: [id] });
    qc.invalidateQueries({ queryKey });
    qc.invalidateQueries({ queryKey: ["/call-queue"] });
  }

  if (isLoading) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;
  if (!data) return <div className="p-6 text-sm text-muted-foreground">Not found.</div>;
  const l = data;
  const converted = l.status === "CONVERTED" || !!l.convertedAt;

  return (
    <>
      <PageHeader
        title={l.name}
        description={[l.title, l.company].filter(Boolean).join(" · ") || undefined}
        actions={
          <>
            {l.status !== "CONVERTED" && <Button onClick={convert}><ArrowRightCircle className="mr-1 h-4 w-4" />Convert</Button>}
            {!converted && <Button variant="outline" onClick={addToQueue}><PhoneCall className="mr-1 h-4 w-4" />Add to my queue</Button>}
            <Button variant="outline" onClick={del}><Trash2 className="mr-1 h-4 w-4" />Delete</Button>
          </>
        }
      />
      <div className="space-y-4 p-6">
        <div className="flex items-center gap-2">
          <Badge variant="secondary">{l.status}</Badge>
          {l.score != null && <Badge variant="outline">Score {l.score}</Badge>}
          {l.source && <span className="text-sm text-muted-foreground">via {l.source}</span>}
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <Card>
            <CardHeader><CardTitle className="text-base">Contact</CardTitle></CardHeader>
            <CardContent>
              <KV k="Email" v={l.email ? <a className="hover:underline" href={`mailto:${l.email}`}>{l.email}</a> : null} />
              <KV k="Secondary email" v={l.secondaryEmail} />
              <KV k="Phone" v={l.phone} />
              <KV k="Mobile" v={l.mobile} />
              <KV k="Website" v={l.website ? <a className="hover:underline" href={l.website} target="_blank" rel="noreferrer">{l.website}</a> : null} />
              <KV k="LinkedIn" v={l.linkedinUrl ? <a className="hover:underline" href={l.linkedinUrl} target="_blank" rel="noreferrer">Profile</a> : null} />
              <KV k="Skype" v={l.skypeId} />
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-base">Attribution &amp; scoring</CardTitle></CardHeader>
            <CardContent>
              <KV k="Source" v={l.source} />
              <KV k="Score" v={l.score} />
              <KV k="Campaign" v={l.campaign?.name ?? null} />
              <KV k="Lead date" v={fmtDate(l.leadDate)} />
              <KV k="Industry" v={l.leadIndustry} />
              <KV k="Degree" v={l.leadDegree} />
              <KV k="Geography" v={l.leadGeography} />
              <KV k="Annual revenue" v={money(l.annualRevenue)} />
              <KV k="Employees" v={l.noOfEmployees} />
              <KV k="Owner" v={ownerName(l.ownerId)} />
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-base">Address</CardTitle></CardHeader>
            <CardContent>
              <KV k="Street" v={l.street} />
              <KV k="City" v={l.city} />
              <KV k="State" v={l.state} />
              <KV k="Zip" v={l.zip} />
              <KV k="Country" v={l.country} />
            </CardContent>
          </Card>
          {converted && (
            <Card>
              <CardHeader><CardTitle className="text-base">Converted to</CardTitle></CardHeader>
              <CardContent>
                <KV k="Account" v={l.account ? <Link className="hover:underline" to={`/accounts/${l.account.id}`}>{l.account.name}</Link> : null} />
                <KV k="Contact" v={l.contact ? <Link className="hover:underline" to={`/contacts/${l.contact.id}`}>{l.contact.name}</Link> : null} />
                <KV k="Deal" v={l.deal ? <Link className="hover:underline" to={`/deals/${l.deal.id}`}>{l.deal.name}</Link> : null} />
                <KV k="Converted" v={fmtDate(l.convertedAt)} />
              </CardContent>
            </Card>
          )}
        </div>

        <Card>
          <CardHeader><CardTitle className="text-base">Tags</CardTitle></CardHeader>
          <CardContent>
            <TagsPanel endpoint={`/leads/${id}`} queryKey={queryKey} tags={l.tags ?? []} />
          </CardContent>
        </Card>

        <div className="grid gap-4 md:grid-cols-2">
          <Prose title="Notes" body={l.notes} />
          <Prose title="CBC offerings" body={l.cbcOfferings} />
          <Prose title="Approach" body={l.approach} />
          <Prose title="Triggering signal" body={l.triggeringSignal} />
          <Prose title="Signal evidence" body={l.signalEvidence} />
          <Prose title="Outreach message" body={l.outreachMessage} />
        </div>

        <Card>
          <CardHeader><CardTitle className="text-base">Recent activity</CardTitle></CardHeader>
          <CardContent>
            {l.activities?.length ? (
              <div className="space-y-1">
                {l.activities.map((a: any) => (
                  <div key={a.id} className="flex items-center gap-2 border-b py-1 text-sm last:border-0">
                    <Badge variant="outline">{a.type}</Badge>
                    <span className="truncate">{a.subject ?? a.body ?? ""}</span>
                    <span className="ml-auto whitespace-nowrap text-xs text-muted-foreground">{fmtDate(a.createdAt)}</span>
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-muted-foreground">No activity logged.</p>}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
